
import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from '@/components/ui/table';
import { toast } from 'sonner';
import { Trash2, Plus, Download, Upload } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { checkProhibitedWordsTable, addDefaultProhibitedWords } from '@/functions/create-prohibited-words-table';

interface ProhibitedWord {
  id: string;
  word: string;
  created_at: string;
  category: string;
}

// Local storage key for backup
const PROHIBITED_WORDS_KEY = 'fomo_prohibited_words';

export const ProhibitedWordsManager = () => {
  const [prohibitedWords, setProhibitedWords] = useState<ProhibitedWord[]>([]);
  const [newWord, setNewWord] = useState('');
  const [newCategory, setNewCategory] = useState('general');
  const [isDeleting, setIsDeleting] = useState<string | null>(null);

  // Use react-query to manage database state
  const queryClient = useQueryClient();
  
  const { data: words, isLoading, error } = useQuery({
    queryKey: ['prohibited-words'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('prohibited_words')
        .select('*')
        .order('created_at', { ascending: false });
        
      if (error) throw error;
      return data as ProhibitedWord[];
    }
  });
  
  // Set words when they load from the query
  useEffect(() => {
    if (words) {
      setProhibitedWords(words);
    }
  }, [words]);
  
  // Check if table exists and add default words if needed
  useEffect(() => {
    checkTableAndInitialize();
  }, []);
  
  const checkTableAndInitialize = async () => {
    try {
      // Check if the prohibited_words table exists
      const { exists, count, error } = await checkProhibitedWordsTable();
      
      if (!exists || error) {
        console.error('Prohibited words table does not exist or there was an error:', error);
        toast.error('There was a problem loading the prohibited words system');
        return;
      }
      
      // If table exists but is empty, add default words
      if (exists && count === 0) {
        await addDefaultWords();
      }
    } catch (err) {
      console.error('Error checking/initializing prohibited words table:', err);
    }
  };
  
  const addDefaultWords = async () => {
    try {
      const result = await addDefaultProhibitedWords();
      
      if (result.success) {
        // Refresh data
        queryClient.invalidateQueries({ queryKey: ['prohibited-words'] });
        toast.success('Default prohibited words added');
      } else {
        console.error('Error adding default prohibited words:', result.error);
        toast.error('Failed to add default prohibited words');
      }
    } catch (error) {
      console.error('Error adding default prohibited words:', error);
      toast.error('Failed to add default prohibited words');
    }
  };

  const handleAddWord = async () => {
    if (!newWord.trim()) {
      toast.error('Please enter a word');
      return;
    }
    
    const wordToAdd = newWord.toLowerCase().trim();
    
    // Check if word already exists
    const exists = prohibitedWords.some(
      (item) => item.word.toLowerCase() === wordToAdd
    );
    
    if (exists) {
      toast.error('This word is already in the list');
      return;
    }

    const newProhibitedWord = {
      word: wordToAdd,
      category: newCategory || 'general', // Use the category field
    };

    try {
      // Add to database
      const { error } = await supabase
        .from('prohibited_words')
        .insert([newProhibitedWord]);
        
      if (error) {
        console.error('Error adding prohibited word:', error);
        throw error;
      }
      
      // Update local state
      queryClient.invalidateQueries({ queryKey: ['prohibited-words'] });
      
      toast.success('Word added to prohibited list');
      setNewWord('');
      setNewCategory('general');
    } catch (error) {
      console.error('Error adding prohibited word:', error);
      toast.error('Failed to add word');
    }
  };

  const handleDeleteWord = async (id: string) => {
    setIsDeleting(id);
    
    try {
      // Delete from database
      const { error } = await supabase
        .from('prohibited_words')
        .delete()
        .eq('id', id);
        
      if (error) {
        throw error;
      }
      
      // Update local state
      const updatedWords = prohibitedWords.filter(word => word.id !== id);
      setProhibitedWords(updatedWords);
      
      // Refresh data
      queryClient.invalidateQueries({ queryKey: ['prohibited-words'] });
      
      toast.success('Word removed from prohibited list');
    } catch (error) {
      console.error('Error deleting prohibited word:', error);
      toast.error('Failed to delete word');
    } finally {
      setIsDeleting(null);
    }
  };
  
  const exportWords = async () => {
    try {
      // Get the latest data from the database to ensure we export the most current list
      const { data, error } = await supabase
        .from('prohibited_words')
        .select('*');
        
      if (error) {
        throw error;
      }
      
      const wordListData = JSON.stringify(data, null, 2);
      const blob = new Blob([wordListData], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      
      const a = document.createElement('a');
      a.href = url;
      a.download = 'fomo-prohibited-words.json';
      document.body.appendChild(a);
      a.click();
      
      // Clean up
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      toast.success('Prohibited word list exported successfully');
    } catch (error) {
      console.error('Error exporting words:', error);
      toast.error('Failed to export word list');
    }
  };
  
  const importWords = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const reader = new FileReader();
    reader.onload = async (event) => {
      try {
        const content = event.target?.result as string;
        const importedWords = JSON.parse(content) as ProhibitedWord[];
        
        // Validate the imported data structure
        if (!Array.isArray(importedWords) || 
            !importedWords.every(word => 
              typeof word.word === 'string' && 
              typeof word.category === 'string')) {
          throw new Error('Invalid format');
        }
        
        // Prepare words for insert/upsert - remove IDs and timestamps that might conflict
        const wordsToUpsert = importedWords.map(({ word, category }) => ({
          word,
          category
        }));
        
        // Delete existing words first (full replacement)
        const { error: deleteError } = await supabase
          .from('prohibited_words')
          .delete()
          .neq('id', '00000000-0000-0000-0000-000000000000'); // Delete all
          
        if (deleteError) {
          throw deleteError;
        }
        
        // Insert new words
        const { error: insertError } = await supabase
          .from('prohibited_words')
          .insert(wordsToUpsert);
          
        if (insertError) {
          throw insertError;
        }
        
        // Refresh the data
        queryClient.invalidateQueries({ queryKey: ['prohibited-words'] });
        
        toast.success('Words imported successfully to database');
      } catch (error) {
        console.error('Error importing words:', error);
        toast.error('Failed to import words. Please check file format.');
      }
    };
    reader.readAsText(file);
    
    // Reset the input element
    e.target.value = '';
  };

  return (
    <div className="space-y-6">
      <div className="flex gap-2 flex-wrap">
        <Input
          placeholder="Add a prohibited word"
          value={newWord}
          onChange={(e) => setNewWord(e.target.value)}
          className="max-w-sm"
        />
        <select 
          value={newCategory}
          onChange={(e) => setNewCategory(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-md"
        >
          <option value="general">General</option>
          <option value="racist">Racist</option>
          <option value="hate-speech">Hate Speech</option>
          <option value="extremism">Extremism</option>
          <option value="illegal-content">Illegal Content</option>
          <option value="harassment">Harassment</option>
        </select>
        <Button 
          onClick={handleAddWord} 
          disabled={!newWord.trim()}
          className="gap-1"
        >
          <Plus size={16} />
          Add Word
        </Button>
        
        <div className="flex ml-auto gap-2">
          <Button
            variant="outline"
            onClick={exportWords}
            title="Export word list"
            className="gap-1"
          >
            <Download size={16} />
            Export
          </Button>
          
          <Button
            variant="outline"
            className="gap-1"
            onClick={() => document.getElementById('import-words')?.click()}
            title="Import word list"
          >
            <Upload size={16} />
            Import
          </Button>
          <input
            type="file"
            id="import-words"
            onChange={importWords}
            accept=".json"
            className="hidden"
          />
        </div>
      </div>

      <div className="border rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Prohibited Word</TableHead>
              <TableHead>Category</TableHead>
              <TableHead>Added On</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={4} className="text-center py-8">
                  Loading word list...
                </TableCell>
              </TableRow>
            ) : error ? (
              <TableRow>
                <TableCell colSpan={4} className="text-center py-8 text-red-500">
                  Error loading words. Please refresh.
                </TableCell>
              </TableRow>
            ) : prohibitedWords.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} className="text-center py-8 text-muted-foreground">
                  No prohibited words added yet. Add some words to filter out unwanted content.
                </TableCell>
              </TableRow>
            ) : (
              prohibitedWords.map((word) => (
                <TableRow key={word.id}>
                  <TableCell className="font-medium">{word.word}</TableCell>
                  <TableCell>{word.category || 'general'}</TableCell>
                  <TableCell>{new Date(word.created_at).toLocaleDateString()}</TableCell>
                  <TableCell>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDeleteWord(word.id)}
                      disabled={isDeleting === word.id}
                      className="text-red-500 hover:text-red-700 hover:bg-red-100"
                    >
                      <Trash2 size={16} />
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};
